import React, { useState, useMemo } from "react";
import { ComponentRenderer } from "../ComponentRenderer";
import { ComponentConfig, ComponentType, ValidationRule, LayoutTypes } from "../../services/types/ComponentTypes";
import { hrmCardStyles } from "../../pages/hrmStyles";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import { navigateToView } from "../../state/slices/navigationSlice";
import { isComponentVisible, getUserRole } from "../../utils/visibilityUtils";
import { useDarkMode } from "../../contexts/DarkModeContext";

const sectionStyles = (isDarkMode: boolean) =>
  isDarkMode
    ? {
        backgroundColor: "#1e293b",
        borderRadius: "12px",
        padding: "16px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
      }
    : hrmCardStyles.section;

type CollapsibleSectionProps = {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  isDarkMode?: boolean;
};

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  isExpanded,
  onToggle,
  children,
  isDarkMode = false,
}) => {
  const sectionStyle = sectionStyles(isDarkMode);
  const borderColor = isDarkMode ? "#334155" : "#E5E7EB";
  const titleColor = isDarkMode ? "#f1f5f9" : "#0F172A";
  const arrowColor = isDarkMode ? "#94a3b8" : "#64748B";
  const contentBg = isDarkMode ? "#1e293b" : "transparent";
  const headerHoverBg = isDarkMode ? "rgba(148, 163, 184, 0.08)" : "rgba(15, 23, 42, 0.04)";

  return (
    <div style={{ ...sectionStyle, marginBottom: "12px" }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 14px",
          backgroundColor: "transparent",
          border: "none",
          borderBottom: `1px solid ${borderColor}`,
          cursor: "pointer",
          textAlign: "left",
          borderRadius: "10px",
          transition: "background-color 0.15s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = headerHoverBg;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
        }}
      >
        <h3
          style={{
            fontSize: "14px",
            fontWeight: 600,
            color: titleColor,
            margin: 0,
            letterSpacing: "0.2px",
          }}
        >
          {title}
        </h3>
        <span
          aria-hidden="true"
          style={{
            width: 18,
            height: 18,
            color: arrowColor,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "transform 0.2s",
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.5 7.75L10 12.25L14.5 7.75"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      <div
        style={{
          maxHeight: isExpanded ? "5000px" : "0",
          overflow: isExpanded ? "visible" : "hidden",
          transition: "max-height 0.3s ease-in-out",
          backgroundColor: contentBg,
        }}
      >
        <div style={{ padding: "14px" }}>{children}</div>
      </div>
    </div>
  );
};

type TabConfigs = {
  [tabName: string]: {
    visibility?: number[];
    sections: Array<{
      sectionId: string;
      title: string;
      enabled?: boolean;
      enabledForCountries?: string[];
      disabledForCountries?: string[];
      fields: any[];
    }>;
  };
};

type ProfileAddTabsProps = {
  formData?: Record<string, any>;
  onFormDataChange?: (name: string, value: any) => void;
  onActionTrigger?: (actionId: string, data?: any) => void;
  // Legacy props (for backward compatibility)
  aboutTabConfig?: ComponentConfig[];
  profileTabConfig?: ComponentConfig[];
  jobTabConfig?: ComponentConfig[];
  documentsTabConfig?: ComponentConfig[];
  assetsTabConfig?: ComponentConfig[];
  isEditMode?: boolean;
  // Props from backend JSON or direct ComponentConfig arrays
  moduleName?: string;
  tabs?: string[];
  tabConfigs?: TabConfigs;
  country?: string | null;
  style?: React.CSSProperties;
  id?: string;
};

export const ProfileAddTabs: React.FC<ProfileAddTabsProps> = ({
  formData = {},
  onFormDataChange,
  onActionTrigger,
  // Legacy props (for backward compatibility)
  aboutTabConfig: legacyAboutTabConfig = [],
  profileTabConfig: legacyProfileTabConfig = [],
  jobTabConfig: legacyJobTabConfig = [],
  documentsTabConfig: legacyDocumentsTabConfig = [],
  assetsTabConfig: legacyAssetsTabConfig = [],
  isEditMode = false,
  // Props from backend JSON
  moduleName,
  tabs,
  tabConfigs,
  country,
  style,
  id,
  ...otherProps
}) => {
  // Navigation hooks and dark mode
  const dispatch = useAppDispatch();
  const navigation = useAppSelector((state) => state.navigation);
  const { isDarkMode } = useDarkMode();

  // Auto-populate read-only Name field from first/middle/last name
  React.useEffect(() => {
    const first = (formData.primary_first_name || "").trim();
    const middle = (formData.primary_middle_name || "").trim();
    const last = (formData.primary_last_name || "").trim();

    // Include middle name when present; skip empty parts
    const parts = [first, middle, last].filter(Boolean);
    const fullName = parts.join(" ");

    if (onFormDataChange && fullName && fullName !== formData.primary_name) {
      onFormDataChange("primary_name", fullName);
    }
    // If all parts are empty, we leave primary_name as-is so we don't overwrite
    // any server-provided value (e.g. when loading existing records).
  }, [
    formData.primary_first_name,
    formData.primary_middle_name,
    formData.primary_last_name,
    formData.primary_name,
    onFormDataChange,
  ]);

  // If marital status is Single, marriage date must be empty/disabled.
  React.useEffect(() => {
    const status = String(formData.primary_marital_status || "").trim().toLowerCase();
    if (status === "single" && onFormDataChange && formData.primary_marriage_date) {
      onFormDataChange("primary_marriage_date", "");
    }
  }, [formData.primary_marital_status, formData.primary_marriage_date, onFormDataChange]);

  // Use tabs from props if provided, otherwise default
  const availableTabs = tabs || ["about", "profile", "job", "documents", "assets"];

  // Filter tabs based on visibility
  const visibleTabs = useMemo(() => {
    if (!tabs || !tabConfigs) {
      if (import.meta.env.DEV) {
        console.log("🔵 [ProfileAddTabs] No tabs or tabConfigs, using availableTabs:", availableTabs);
      }
      return availableTabs; // Fallback to all tabs if no config
    }

    const filtered = tabs.filter((tabName) => {
      const tabConfig = tabConfigs[tabName];
      if (!tabConfig) {
        if (import.meta.env.DEV) {
          console.log(`✅ [ProfileAddTabs] Tab '${tabName}' has no config, showing by default`);
        }
        return true; // Show tab if no config found (backward compatibility)
      }

      // Check visibility using the existing utility
      const isVisible = isComponentVisible(tabConfig.visibility);
      if (import.meta.env.DEV) {
        console.log(`🔍 [ProfileAddTabs] Tab '${tabName}' visibility:`, {
          visibility: tabConfig.visibility,
          isVisible,
          userRole: getUserRole(),
        });
      }
      return isVisible;
    });

    if (import.meta.env.DEV) {
      console.log("📋 [ProfileAddTabs] Visible tabs:", {
        allTabs: tabs,
        visibleTabs: filtered,
        tabConfigs: Object.keys(tabConfigs),
      });
    }

    return filtered;
  }, [tabs, tabConfigs, availableTabs]);

  // Initialize selectedTab with first available tab
  const [selectedTab, setSelectedTab] = useState(visibleTabs[0] || "about");

  // Ensure selectedTab is valid (in case it becomes hidden after filtering)
  const validSelectedTab = useMemo(() => {
    if (visibleTabs.includes(selectedTab)) {
      return selectedTab;
    }
    // If current tab is not visible, switch to first visible tab
    return visibleTabs[0] || "about";
  }, [selectedTab, visibleTabs]);

  // Update selectedTab if it changed due to filtering
  React.useEffect(() => {
    if (validSelectedTab !== selectedTab) {
      setSelectedTab(validSelectedTab);
    }
  }, [validSelectedTab, selectedTab]);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["primary_details", "contact_details"])
  );
  // Track completed tabs
  const [completedTabs, setCompletedTabs] = useState<Set<string>>(new Set());

  // Handle back navigation
  const handleBackClick = () => {
    // If we have a previous view, use it
    if (navigation.previousView && navigation.previousView !== "") {
      dispatch(navigateToView({ view: navigation.previousView, rememberPrevious: false }));
    } else {
      // Default to dashboard/home
      dispatch(navigateToView({ view: "dashboard", rememberPrevious: false }));
    }
  };

  // Inline country-based section filter
  const filterSectionsByCountry = (sections: any[], countryCode?: string | null) => {
    if (!countryCode) return sections;
    return sections.filter((section) => {
      if (section.enabledForCountries) {
        return section.enabledForCountries.includes(countryCode);
      }
      if (section.disabledForCountries) {
        return !section.disabledForCountries.includes(countryCode);
      }
      return true;
    });
  };

  // Convert JSON tabConfigs to ComponentConfig format
  const convertTabConfigToComponentConfig = (
    tabName: string,
    legacyConfig: ComponentConfig[]
  ): ComponentConfig[] => {
    // If tabConfigs provided, use provided config
    if (tabConfigs && tabConfigs[tabName]) {
      const tabConfig = tabConfigs[tabName];
      const filteredSections = filterSectionsByCountry(
        tabConfig.sections || [],
        country
      );

      // Convert each section's fields to ComponentConfig GRID wrapper
      const configs: ComponentConfig[] = [];
      filteredSections.forEach((section) => {
        // Wrap natively configured fields in a GRID component
        configs.push({
          componentType: LayoutTypes.GRID,
          columnNumber: section.fields[0]?.columnNumber || "m1 t2 w3",
          style: { backgroundColor: "transparent" },
          childComponents: section.fields as ComponentConfig[],
        } as ComponentConfig);
      });

      return configs;
    }

    // Fallback to legacy configs for backward compatibility
    return legacyConfig;
  };

  // Convert all tabs
  const aboutTabConfig = useMemo(
    () => convertTabConfigToComponentConfig("about", legacyAboutTabConfig),
    [tabConfigs, country, legacyAboutTabConfig]
  );

  const profileTabConfig = useMemo(
    () => convertTabConfigToComponentConfig("profile", legacyProfileTabConfig),
    [tabConfigs, country, legacyProfileTabConfig]
  );

  const jobTabConfig = useMemo(
    () => convertTabConfigToComponentConfig("job", legacyJobTabConfig),
    [tabConfigs, country, legacyJobTabConfig]
  );

  const documentsTabConfig = useMemo(
    () => convertTabConfigToComponentConfig("documents", legacyDocumentsTabConfig),
    [tabConfigs, country, legacyDocumentsTabConfig]
  );

  const assetsTabConfig = useMemo(
    () => convertTabConfigToComponentConfig("assets", legacyAssetsTabConfig),
    [tabConfigs, country, legacyAssetsTabConfig]
  );

  // Get sections for dynamic rendering (from JSON if available)
  const getSectionsForTab = (tabName: string): Array<{ sectionId: string; title: string }> => {
    if (tabConfigs && tabConfigs[tabName]) {
      const filteredSections = filterSectionsByCountry(
        tabConfigs[tabName].sections || [],
        country
      );
      return filteredSections.map((s) => ({
        sectionId: s.sectionId,
        title: s.title,
      }));
    }
    // Return default sections for legacy mode
    return [];
  };

  // Get config for a specific section by tab and sectionId
  const getConfigBySectionId = (
    tabName: string,
    sectionId: string
  ): ComponentConfig[] => {
    // If using tabConfigs, find section by sectionId
    if (tabConfigs && tabConfigs[tabName]) {
      const section = tabConfigs[tabName].sections.find(
        (s) => s.sectionId === sectionId
      );
      if (section) {
        const filteredSections = filterSectionsByCountry([section], country);
        if (filteredSections.length > 0) {
          // Filter out net salary field from salary_summary section
          let fields = filteredSections[0].fields;
          if (sectionId === "salary_summary") {
            fields = fields.filter(
              (field: any) => field.name !== "salary_net_salary"
            );
          }
          // HRMS rule: Marriage Date should be disabled when Marital Status is Single
          if (tabName === "about") {
            const status = String(formData.primary_marital_status || "").trim().toLowerCase();
            const disableMarriageDate = status === "single";
            if (disableMarriageDate) {
              fields = fields.map((field: any) => {
                if (field?.name === "primary_marriage_date" || field?.Json_Attribute === "marriage_date") {
                  return { ...field, disabled: true };
                }
                return field;
              });
            }
          }
          // Wrap fields into a GRID component directly
          return [{
            componentType: LayoutTypes.GRID,
            columnNumber: fields[0]?.columnNumber || "m1 t2 w3",
            style: { backgroundColor: "transparent" },
            childComponents: fields as ComponentConfig[],
          } as ComponentConfig];
        }
      }
      return [];
    }

    // Fallback to legacy section matching - get configs for the tab
    const tabConfigsMap: Record<string, ComponentConfig[]> = {
      about: aboutTabConfig,
      profile: profileTabConfig,
      job: jobTabConfig,
      documents: documentsTabConfig,
      assets: assetsTabConfig,
    };
    const configs = tabConfigsMap[tabName] || [];
    return getConfigBySection(configs, sectionId);
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  // Validation function to validate all fields in a tab
  const validateTab = (tabName: string, currentFormData: Record<string, any>): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Get all field configs for this tab
    const sections = getSectionsForTab(tabName);
    const allFieldConfigs: ComponentConfig[] = [];

    sections.forEach((section) => {
      const sectionConfigs = getConfigBySectionId(tabName, section.sectionId);
      // Extract field configs from GRID components (they contain childComponents)
      sectionConfigs.forEach((config) => {
        if (config.childComponents) {
          config.childComponents.forEach((child: ComponentConfig) => {
            // Only validate form fields (not layout components)
            if (
              child.componentType === ComponentType.TEXT_FIELD ||
              child.componentType === ComponentType.TEXT_AREA ||
              child.componentType === ComponentType.DATE_FIELD ||
              child.componentType === ComponentType.SELECT_DROPDOWN ||
              child.componentType === ComponentType.SEARCHABLE_DROPDOWN
            ) {
              allFieldConfigs.push(child);
            }
          });
        }
      });
    });

    // If no sections found, try legacy config approach
    if (allFieldConfigs.length === 0) {
      const tabConfigsMap: Record<string, ComponentConfig[]> = {
        about: aboutTabConfig,
        profile: profileTabConfig,
        job: jobTabConfig,
        documents: documentsTabConfig,
        assets: assetsTabConfig,
      };
      const tabConfig = tabConfigsMap[tabName] || [];

      tabConfig.forEach((config) => {
        if (config.childComponents) {
          config.childComponents.forEach((child: ComponentConfig) => {
            if (
              child.componentType === ComponentType.TEXT_FIELD ||
              child.componentType === ComponentType.TEXT_AREA ||
              child.componentType === ComponentType.DATE_FIELD ||
              child.componentType === ComponentType.SELECT_DROPDOWN ||
              child.componentType === ComponentType.SEARCHABLE_DROPDOWN
            ) {
              allFieldConfigs.push(child);
            }
          });
        }
      });
    }

    // Validate each field
    allFieldConfigs.forEach((fieldConfig: any) => {
      const fieldName = fieldConfig.name || fieldConfig.BindValue;
      if (!fieldName) return; // Skip if no field name

      const fieldValue = currentFormData[fieldName];
      // Handle different value types (string, number, null, undefined)
      const fieldValueStr = fieldValue !== undefined && fieldValue !== null ? String(fieldValue) : "";
      // For number fields, 0 is a valid value, so don't treat it as empty
      const isNumberField = fieldConfig.componentType === ComponentType.TEXT_FIELD && fieldConfig.inputType === "number";
      const isEmpty = fieldValue === undefined || fieldValue === null || (fieldValueStr.trim() === "" && !isNumberField);

      // Check required fields
      if (fieldConfig.required && isEmpty) {
        errors.push(`${fieldConfig.label || fieldName} is required`);
        return;
      }

      // Skip validation if field is empty and not required
      if (isEmpty && !fieldConfig.required) {
        return;
      }

      // Validate based on field type
      if (fieldConfig.componentType === ComponentType.TEXT_FIELD) {
        // Text field validations
        if (fieldConfig.inputType === "email" || fieldConfig.enableEmailValidation) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (fieldValueStr && !emailRegex.test(fieldValueStr)) {
            errors.push(`${fieldConfig.label || fieldName}: Please enter a valid email address`);
          }
        }

        if (fieldConfig.inputType === "number" && fieldValueStr) {
          // Allow digits, single decimal point, and up to 2 decimal places
          // Pattern: optional digits, optional decimal point, optional 1-2 digits after decimal
          if (!/^\d*\.?\d{0,2}$/.test(fieldValueStr)) {
            errors.push(`${fieldConfig.label || fieldName}: Please enter a valid number (e.g., 1000 or 1000.50)`);
          }
          // Additional check: don't allow just a decimal point
          if (fieldValueStr === ".") {
            errors.push(`${fieldConfig.label || fieldName}: Please enter a valid number`);
          }
        }

        // Check validation rules
        if (fieldConfig.validation) {
          fieldConfig.validation.forEach((rule: any) => {
            if (rule.rule === ValidationRule.PATTERN || rule.rule === "pattern") {
              const regex = new RegExp(rule.value as string);
              if (fieldValueStr && !regex.test(fieldValueStr)) {
                errors.push(rule.message || `${fieldConfig.label || fieldName}: Invalid format`);
              }
            }
            if ((rule.rule === ValidationRule.MAX_LENGTH || rule.rule === "maxLength") && fieldValueStr && fieldValueStr.length > (rule.value as number)) {
              errors.push(rule.message || `${fieldConfig.label || fieldName}: Maximum ${rule.value} characters allowed`);
            }
            if ((rule.rule === ValidationRule.MIN_LENGTH || rule.rule === "minLength") && fieldValueStr && fieldValueStr.length < (rule.value as number)) {
              errors.push(rule.message || `${fieldConfig.label || fieldName}: Minimum ${rule.value} characters required`);
            }
            if (rule.rule === ValidationRule.EMAIL || rule.rule === "email") {
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (fieldValueStr && !emailRegex.test(fieldValueStr)) {
                errors.push(rule.message || `${fieldConfig.label || fieldName}: Please enter a valid email address`);
              }
            }
          });
        }
      }

      if (fieldConfig.componentType === ComponentType.DATE_FIELD) {
        // Date field validations
        if (fieldValueStr) {
          const selectedDate = new Date(fieldValueStr);
          if (isNaN(selectedDate.getTime())) {
            errors.push(`${fieldConfig.label || fieldName}: Invalid date format`);
            return;
          }

          const today = new Date();
          today.setHours(23, 59, 59, 999);

          if (fieldConfig.preventFutureDateSelection && selectedDate > today) {
            errors.push(`${fieldConfig.label || fieldName}: Date cannot be in the future`);
          }

          if (fieldConfig.minDate) {
            const minDate = new Date(fieldConfig.minDate);
            if (selectedDate < minDate) {
              errors.push(`${fieldConfig.label || fieldName}: Date must be on or after ${minDate.toLocaleDateString()}`);
            }
          }

          // For expiry dates, ensure they're not in the past
          if ((fieldConfig as any).minDateField) {
            const todayStart = new Date();
            todayStart.setHours(0, 0, 0, 0);
            if (selectedDate < todayStart) {
              errors.push(`${fieldConfig.label || fieldName}: Expiry date cannot be in the past`);
            }
          }
        }
      }

      if (fieldConfig.componentType === ComponentType.TEXT_AREA) {
        // TextArea validations
        if (fieldConfig.maxLength && fieldValueStr && fieldValueStr.length > fieldConfig.maxLength) {
          errors.push(`${fieldConfig.label || fieldName}: Maximum ${fieldConfig.maxLength} characters allowed`);
        }
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const tabButtonStyles = (isActive: boolean): React.CSSProperties => ({
    padding: "10px 0",
    backgroundColor: "transparent",
    border: "none",
    borderBottom: isActive ? "2px solid #34D399" : "2px solid transparent",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 600,
    color: isActive
      ? isDarkMode ? "#f1f5f9" : "#0F172A"
      : isDarkMode ? "#94a3b8" : "#64748B",
    marginRight: "20px",
    transition: "all 0.2s ease",
  });

  // Filter configs by section - returns GRID components that contain matching fields
  const getConfigBySection = (configs: ComponentConfig[], sectionName: string): ComponentConfig[] => {
    return configs.filter((config) => {
      // Check if this config's childComponents contain fields matching the section
      if (config.childComponents && config.childComponents.length > 0) {
        return config.childComponents.some((child: ComponentConfig) => {
          const name = (child as any).name || "";
          // Check if the name contains the section identifier
          if (sectionName === "primary") {
            return name.includes("primary_");
          } else if (sectionName === "contact") {
            return name.includes("contact_");
          } else if (sectionName === "emergency_contact_1") {
            return name.includes("emergency_contact_1");
          } else if (sectionName === "emergency_contact_2") {
            return name.includes("emergency_contact_2");
          } else if (sectionName === "address") {
            return name.includes("address_");
          } else if (sectionName === "pan") {
            return name.startsWith("pan_");
          } else if (sectionName === "aadhaar") {
            return name.startsWith("aadhaar_");
          } else if (sectionName === "employment_history") {
            return name.includes("employment_history_");
          } else if (sectionName === "about") {
            return name === "about";
          } else if (sectionName === "what_i_love") {
            return name === "what_i_love";
          } else if (sectionName === "interests") {
            return name === "interests";
          } else if (sectionName === "job") {
            return name.startsWith("job_");
          } else if (sectionName === "organization") {
            return name.startsWith("organization_");
          } else if (sectionName === "employee_time") {
            return name.startsWith("employee_time_");
          } else if (sectionName === "other") {
            return name.startsWith("other_");
          } else if (sectionName === "identity") {
            return name.startsWith("identity_");
          }
          return false;
        });
      }
      return false;
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Title with Back Button */}
      <div
        className="flex items-center gap-3 py-1 mb-4"
        style={{
          paddingLeft: "0",
          marginBottom: "8px"
        }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={handleBackClick}
            className="flex items-center justify-center"
            style={{
              backgroundColor: isDarkMode ? "#334155" : "#FFFFFF",
              border: isDarkMode ? "1px solid #475569" : "1px solid #E5E7EB",
              padding: "8px 12px",
              cursor: "pointer",
              fontSize: "24px",
              color: isDarkMode ? "#94a3b8" : "#64748B",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              transition: "background-color 0.2s, color 0.2s",
              boxShadow: isDarkMode ? "0 1px 2px 0 rgba(0, 0, 0, 0.2)" : "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDarkMode ? "#475569" : "#F9FAFB";
              e.currentTarget.style.color = isDarkMode ? "#f1f5f9" : "#0F172A";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isDarkMode ? "#334155" : "#FFFFFF";
              e.currentTarget.style.color = isDarkMode ? "#94a3b8" : "#64748B";
            }}
            aria-label="Go back"
            title="Go back"
          >
            <span style={{ fontSize: "24px", lineHeight: "1" }}>&#8249;</span>
          </button>
          <h1
            className={`text-[22px] font-semibold m-0 ${isDarkMode ? "text-slate-100" : "text-gray-800"}`}
          >
            {isEditMode ? "Edit Profile" : "Add Profile"}
          </h1>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          borderBottom: isDarkMode ? "1px solid #334155" : "1px solid #E5E7EB",
          padding: "0 4px",
        }}
      >
        {visibleTabs.includes("about") && (
          <button
            type="button"
            style={tabButtonStyles(selectedTab === "about")}
            onClick={() => setSelectedTab("about")}
          >
            ABOUT
          </button>
        )}
        {visibleTabs.includes("profile") && (
          <button
            type="button"
            style={tabButtonStyles(selectedTab === "profile")}
            onClick={() => setSelectedTab("profile")}
          >
            PROFILE
          </button>
        )}
        {visibleTabs.includes("job") && (
          <button
            type="button"
            style={tabButtonStyles(selectedTab === "job")}
            onClick={() => setSelectedTab("job")}
          >
            JOB
          </button>
        )}
        {visibleTabs.includes("documents") && (
          <button
            type="button"
            style={tabButtonStyles(selectedTab === "documents")}
            onClick={() => setSelectedTab("documents")}
          >
            DOCUMENTS
          </button>
        )}
        {visibleTabs.includes("assets") && (
          <button
            type="button"
            style={tabButtonStyles(selectedTab === "assets")}
            onClick={() => setSelectedTab("assets")}
          >
            ASSETS
          </button>
        )}
        {visibleTabs.includes("salary") && (
          <button
            type="button"
            style={tabButtonStyles(selectedTab === "salary")}
            onClick={() => setSelectedTab("salary")}
          >
            SALARY
          </button>
        )}
        {visibleTabs.includes("leave") && (
          <button
            type="button"
            style={tabButtonStyles(selectedTab === "leave")}
            onClick={() => setSelectedTab("leave")}
          >
            LEAVE
          </button>
        )}
      </div>

      {/* Tab Content - ABOUT */}
      <div
        style={{
          display: selectedTab === "about" ? "flex" : "none",
          flexDirection: "column",
          gap: "12px"
        }}
      >
        {(() => {
          // Get sections from JSON config or use defaults
          const sections = getSectionsForTab("about");
          if (sections.length > 0) {
            // Render sections from JSON config
            return sections.map((section) => (
              <CollapsibleSection
                key={section.sectionId}
                title={section.title}
                isExpanded={expandedSections.has(section.sectionId)}
                onToggle={() => toggleSection(section.sectionId)}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySectionId("about", section.sectionId)}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>
            ));
          }
          // Fallback to legacy hardcoded sections
          return (
            <>
              <CollapsibleSection
                title="Primary Details"
                isExpanded={expandedSections.has("primary_details")}
                onToggle={() => toggleSection("primary_details")}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySection(aboutTabConfig, "primary").length > 0 ? getConfigBySection(aboutTabConfig, "primary") : []}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>

              <CollapsibleSection
                title="Contact Details"
                isExpanded={expandedSections.has("contact_details")}
                onToggle={() => toggleSection("contact_details")}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySection(aboutTabConfig, "contact")}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>

              <CollapsibleSection
                title="Emergency Contact 1"
                isExpanded={expandedSections.has("emergency_contact_1")}
                onToggle={() => toggleSection("emergency_contact_1")}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySection(aboutTabConfig, "emergency_contact_1")}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>

              <CollapsibleSection
                title="Emergency Contact 2"
                isExpanded={expandedSections.has("emergency_contact_2")}
                onToggle={() => toggleSection("emergency_contact_2")}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySection(aboutTabConfig, "emergency_contact_2")}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>

              <CollapsibleSection
                title="Addresses"
                isExpanded={expandedSections.has("addresses")}
                onToggle={() => toggleSection("addresses")}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySection(aboutTabConfig, "address")}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>

              <CollapsibleSection
                title="Identity Information - PAN Card"
                isExpanded={expandedSections.has("pan_card")}
                onToggle={() => toggleSection("pan_card")}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySection(aboutTabConfig, "pan")}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>

              <CollapsibleSection
                title="Identity Information - Photo ID (Aadhaar)"
                isExpanded={expandedSections.has("aadhaar")}
                onToggle={() => toggleSection("aadhaar")}
              >
                <ComponentRenderer
                  config={getConfigBySection(aboutTabConfig, "aadhaar")}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>

              <CollapsibleSection
                title="Identity Documents (Emirates ID, Passport, Visa)"
                isExpanded={expandedSections.has("identity_documents")}
                onToggle={() => toggleSection("identity_documents")}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySection(aboutTabConfig, "identity")}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>

              <CollapsibleSection
                title="History of Employment"
                isExpanded={expandedSections.has("employment_history")}
                onToggle={() => toggleSection("employment_history")}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySection(aboutTabConfig, "employment_history")}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>
            </>
          );
        })()}
      </div>

      {/* Tab Content - PROFILE */}
      <div
        style={{
          display: selectedTab === "profile" ? "flex" : "none",
          flexDirection: "column",
          gap: "12px"
        }}
      >
        {(() => {
          const sections = getSectionsForTab("profile");
          if (sections.length > 0) {
            return sections.map((section) => (
              <CollapsibleSection
                key={section.sectionId}
                title={section.title}
                isExpanded={expandedSections.has(section.sectionId)}
                onToggle={() => toggleSection(section.sectionId)}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySectionId("profile", section.sectionId)}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>
            ));
          }
          // Fallback to legacy
          return (
            <>
              <CollapsibleSection
                title="About"
                isExpanded={expandedSections.has("about")}
                onToggle={() => toggleSection("about")}
              >
                <ComponentRenderer
                  config={getConfigBySection(profileTabConfig, "about")}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>

              <CollapsibleSection
                title="What I love about my job?"
                isExpanded={expandedSections.has("what_i_love")}
                onToggle={() => toggleSection("what_i_love")}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySection(profileTabConfig, "what_i_love")}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>

              <CollapsibleSection
                title="My interests and hobbies"
                isExpanded={expandedSections.has("interests")}
                onToggle={() => toggleSection("interests")}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySection(profileTabConfig, "interests")}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>
            </>
          );
        })()}
      </div>

      {/* Tab Content - JOB */}
      <div
        style={{
          display: selectedTab === "job" ? "flex" : "none",
          flexDirection: "column",
          gap: "12px"
        }}
      >
        {(() => {
          const sections = getSectionsForTab("job");
          if (sections.length > 0) {
            return sections.map((section) => (
              <CollapsibleSection
                key={section.sectionId}
                title={section.title}
                isExpanded={expandedSections.has(section.sectionId)}
                onToggle={() => toggleSection(section.sectionId)}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySectionId("job", section.sectionId)}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>
            ));
          }
          // Fallback to legacy
          return (
            <>
              <CollapsibleSection
                title="Job Details"
                isExpanded={expandedSections.has("job_details")}
                onToggle={() => toggleSection("job_details")}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySection(jobTabConfig, "job")}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>

              <CollapsibleSection
                title="Organization"
                isExpanded={expandedSections.has("organization")}
                onToggle={() => toggleSection("organization")}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySection(jobTabConfig, "organization")}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>

              <CollapsibleSection
                title="Employee Time"
                isExpanded={expandedSections.has("employee_time")}
                onToggle={() => toggleSection("employee_time")}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySection(jobTabConfig, "employee_time")}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>

              <CollapsibleSection
                title="Other"
                isExpanded={expandedSections.has("other")}
                onToggle={() => toggleSection("other")}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySection(jobTabConfig, "other")}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>
            </>
          );
        })()}
      </div>

      {/* Tab Content - DOCUMENTS */}
      <div
        style={{
          display: selectedTab === "documents" ? "flex" : "none",
          flexDirection: "column",
          gap: "12px"
        }}
      >
        {(() => {
          const sections = getSectionsForTab("documents");
          if (sections.length > 0) {
            return sections.map((section) => (
              <CollapsibleSection
                key={section.sectionId}
                title={section.title}
                isExpanded={expandedSections.has(section.sectionId)}
                onToggle={() => toggleSection(section.sectionId)}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySectionId("documents", section.sectionId)}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>
            ));
          }
          // Fallback to legacy
          return (
            <CollapsibleSection
              title="Upload Documents"
              isExpanded={expandedSections.has("upload_documents")}
              onToggle={() => toggleSection("upload_documents")}
              isDarkMode={isDarkMode}
            >
              <ComponentRenderer
                config={documentsTabConfig}
                formData={formData}
                onFormDataChange={onFormDataChange}
                onActionTrigger={onActionTrigger}
              />
            </CollapsibleSection>
          );
        })()}
      </div>

      {/* Tab Content - ASSETS */}
      <div
        style={{
          display: selectedTab === "assets" ? "flex" : "none",
          flexDirection: "column",
          gap: "12px"
        }}
      >
        {(() => {
          const sections = getSectionsForTab("assets");
          if (sections.length > 0) {
            return sections.map((section) => (
              <CollapsibleSection
                key={section.sectionId}
                title={section.title}
                isExpanded={expandedSections.has(section.sectionId)}
                onToggle={() => toggleSection(section.sectionId)}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySectionId("assets", section.sectionId)}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>
            ));
          }
          // Fallback to legacy
          return (
            <CollapsibleSection
              title="Assign Assets"
              isExpanded={expandedSections.has("assign_assets")}
              onToggle={() => toggleSection("assign_assets")}
              isDarkMode={isDarkMode}
            >
              <ComponentRenderer
                config={assetsTabConfig}
                formData={formData}
                onFormDataChange={onFormDataChange}
                onActionTrigger={onActionTrigger}
              />
            </CollapsibleSection>
          );
        })()}
      </div>

      {/* Tab Content - SALARY */}
      <div
        style={{
          display: selectedTab === "salary" ? "flex" : "none",
          flexDirection: "column",
          gap: "12px"
        }}
      >
        {(() => {
          const sections = getSectionsForTab("salary");

          // Filter out hidden sections: allowances, deductions, additional_info
          const visibleSections = sections.filter((section) => {
            return section.sectionId !== "allowances" &&
              section.sectionId !== "deductions" &&
              section.sectionId !== "additional_info";
          });

          // Reorder sections: basic_salary_info, bank_details, salary_summary
          const orderedSections = visibleSections.sort((a, b) => {
            const order = ["basic_salary_info", "bank_details", "salary_summary"];
            const indexA = order.indexOf(a.sectionId);
            const indexB = order.indexOf(b.sectionId);
            // If section not in order array, keep original position
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
          });

          if (orderedSections.length > 0) {
            return orderedSections.map((section) => (
              <CollapsibleSection
                key={section.sectionId}
                title={section.title}
                isExpanded={expandedSections.has(section.sectionId)}
                onToggle={() => toggleSection(section.sectionId)}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySectionId("salary", section.sectionId)}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>
            ));
          }
          return null;
        })()}
      </div>

      {/* Tab Content - LEAVE */}
      <div
        style={{
          display: selectedTab === "leave" ? "flex" : "none",
          flexDirection: "column",
          gap: "12px"
        }}
      >
        {(() => {
          const sections = getSectionsForTab("leave");
          if (sections.length > 0) {
            return sections.map((section) => (
              <CollapsibleSection
                key={section.sectionId}
                title={section.title}
                isExpanded={expandedSections.has(section.sectionId)}
                onToggle={() => toggleSection(section.sectionId)}
                isDarkMode={isDarkMode}
              >
                <ComponentRenderer
                  config={getConfigBySectionId("leave", section.sectionId)}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  onActionTrigger={onActionTrigger}
                />
              </CollapsibleSection>
            ));
          }
          return null;
        })()}
      </div>

      {/* Action Buttons - Fixed at bottom */}
      <div
        style={{
          position: "sticky",
          bottom: 0,
          backgroundColor: isDarkMode ? "#1e293b" : "#FFFFFF",
          padding: "12px",
          borderTop: isDarkMode ? "1px solid #334155" : "1px solid #E5E7EB",
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
          zIndex: 10,
        }}
      >
        <button
          type="button"
          onClick={() => onActionTrigger?.("cancel_profile_details")}
          style={{
            padding: "10px 18px",
            backgroundColor: "#6B7280",
            color: "#FFFFFF",
            border: "none",
            borderRadius: "4px",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={async () => {
            const currentIndex = visibleTabs.indexOf(selectedTab);
            const isLastTab = currentIndex === visibleTabs.length - 1;

            // Validate current tab before saving
            const validation = validateTab(selectedTab, formData);

            if (!validation.isValid) {
              // Show validation errors and prevent navigation
              const errorMessage = validation.errors.length > 0
                ? validation.errors[0] // Show first error
                : "Please complete all required fields";
              alert(errorMessage);

              // Expand all sections to show errors
              const sections = getSectionsForTab(selectedTab);
              setExpandedSections((prev) => {
                const newSet = new Set(prev);
                sections.forEach((section) => newSet.add(section.sectionId));
                return newSet;
              });

              return; // Don't proceed with save
            }

            // Validation passed - proceed with save
            try {
              // Call save action - it will throw if there's an error
              await onActionTrigger?.("save_tab_data", {
                tab: selectedTab,
                formData: formData,
                isLastTab: isLastTab,
              });

              // Only navigate if save was successful (no error thrown)
              // If not last tab, move to next tab
              if (!isLastTab) {
                const nextTab = visibleTabs[currentIndex + 1];
                setSelectedTab(nextTab);
                setCompletedTabs((prev) => new Set([...prev, selectedTab]));

                // Expand first section of next tab for better UX
                const nextTabSections = getSectionsForTab(nextTab);
                if (nextTabSections.length > 0) {
                  setExpandedSections((prev) => {
                    const newSet = new Set(prev);
                    newSet.add(nextTabSections[0].sectionId);
                    return newSet;
                  });
                }
              }
            } catch (error) {
              // Error handling is done in handleActionTrigger - don't navigate on error
              console.error("Error in save & continue:", error);
              // Error toast is already shown in HrmProfileAdd.tsx
            }
          }}
          style={{
            padding: "10px 18px",
            backgroundColor: "rgb(10 168 76 / var(--tw-bg-opacity))",
            color: "#FFFFFF",
            border: "none",
            borderRadius: "4px",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {(() => {
            const currentIndex = visibleTabs.indexOf(selectedTab);
            const isLastTab = currentIndex === visibleTabs.length - 1;
            return isLastTab ? "Final Submit" : "Save & Continue";
          })()}
        </button>
      </div>
    </div>
  );
};

